/**
 * GoogleMapsProvider
 * -----------------------------------------------------------------------------
 * Wraps the app with the Google Maps JS SDK and runs a one-time health probe.
 *
 * REQUIRED GOOGLE CLOUD CONFIGURATION (must be done in console.cloud.google.com
 * for the project that owns REACT_APP_GOOGLE_MAPS_API_KEY):
 *
 *   1. APIs & Services → Library → Enable each of these:
 *        • Maps JavaScript API     (renders the map + loads other libs)
 *        • Places API (New)        (autocomplete suggestions, place details)
 *        • Directions API          (route + distance + duration)
 *        • Geocoding API           (reverse-geocode "Use My Current Location")
 *
 *   2. Billing → Link a billing account
 *        Maps requires billing to be ENABLED on the project even though the
 *        first $200/month is free. Without billing every request returns
 *        REQUEST_DENIED / ApiNotActivatedMapError.
 *
 *   3. APIs & Services → Credentials → <your key> → Application restrictions
 *        Allow these HTTP referrers (use exact patterns, replace SLASH-STAR
 *        with the actual `/` + `*` characters — written here as words to avoid
 *        terminating this JSDoc block):
 *          - http://localhost:STAR/STAR
 *          - http://127.0.0.1:STAR/STAR
 *          - https://STAR.preview.emergentagent.com/STAR
 *          - https://<your-production-domain>/STAR
 *        For initial testing you may temporarily set restrictions to None.
 *        Under API restrictions: restrict to the 4 APIs listed above.
 *
 *   4. Quotas: default quota is high; if you exceed it Google will return
 *        OVER_QUERY_LIMIT — the UI already handles this gracefully.
 *
 * LOADING / PERFORMANCE
 *   • @vis.gl/react-google-maps loads the JS SDK lazily (async + dedupe).
 *   • Libraries are requested on-demand via useMapsLibrary(...) hooks.
 *   • The probe below fetches a 1-byte response from the Geocoding REST
 *     endpoint so we know early whether the key + APIs + billing are good
 *     and can show a banner to the operator instead of silently failing.
 * -----------------------------------------------------------------------------
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// status: "loading" | "ok" | "missing_key" | "api_not_enabled" |
//         "referer_blocked" | "billing_disabled" | "quota" | "unknown_error"
const MapsStatusContext = createContext({ status: "loading", message: "" });

export const useMapsStatus = () => useContext(MapsStatusContext);

const classifyError = (errorMessage = "") => {
  const m = errorMessage.toLowerCase();
  if (m.includes("not activated") || m.includes("not been used")) return "api_not_enabled";
  if (m.includes("referer") || m.includes("referrer")) return "referer_blocked";
  if (m.includes("billing")) return "billing_disabled";
  if (m.includes("quota") || m.includes("over_query_limit")) return "quota";
  if (m.includes("api key") || m.includes("invalidkey")) return "invalid_key";
  return "unknown_error";
};

const probeApiKey = async () => {
  if (!API_KEY) return { status: "missing_key", message: "REACT_APP_GOOGLE_MAPS_API_KEY is not set" };
  try {
    // Cheap call: geocode the literal "test" string. ZERO_RESULTS is OK,
    // REQUEST_DENIED tells us the API/billing is not configured.
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=test&key=${API_KEY}`;
    const resp = await fetch(url);
    const data = await resp.json();
    if (data.status === "OK" || data.status === "ZERO_RESULTS") {
      return { status: "ok", message: "" };
    }
    return {
      status: classifyError(data.error_message || data.status),
      message: data.error_message || data.status || "Unknown maps error",
    };
  } catch (err) {
    return { status: "unknown_error", message: String(err) };
  }
};

const GoogleMapsProvider = ({ children }) => {
  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    let cancelled = false;
    probeApiKey().then((r) => {
      if (!cancelled) setState(r);
    });
    return () => { cancelled = true; };
  }, []);

  const ctxValue = state;

  if (!API_KEY) {
    return (
      <MapsStatusContext.Provider value={{ status: "missing_key", message: "" }}>
        {children}
      </MapsStatusContext.Provider>
    );
  }

  return (
    <MapsStatusContext.Provider value={ctxValue}>
      <APIProvider
        apiKey={API_KEY}
        libraries={["places", "routes", "geocoding"]}
        onLoad={() => {/* SDK loaded, autocomplete will be enabled when used */}}
      >
        {children}
      </APIProvider>
    </MapsStatusContext.Provider>
  );
};

export const isMapsConfigured = !!API_KEY;

export default GoogleMapsProvider;
